import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Scale, ArrowLeft, Plus, Play, FileText, Search, Shield, 
  ClipboardCheck, Edit3, Workflow, Clock, CheckCircle, XCircle,
  ChevronRight, Trash2, Copy, Settings, MoreVertical, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AuthContext } from "@/App";

// Use relative URL in production (deployed), or env variable in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const iconMap = {
  FileText: FileText,
  Search: Search,
  Shield: Shield,
  ClipboardCheck: ClipboardCheck,
  Edit3: Edit3,
  Workflow: Workflow
};

const Workflows = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [activeTab, setActiveTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [myWorkflows, setMyWorkflows] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [executionVariables, setExecutionVariables] = useState({});
  const [executing, setExecuting] = useState(false);
  const [stats, setStats] = useState({ total: 0, completed: 0, running: 0, failed: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, workflowsRes, executionsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/workflows/templates`),
        axios.get(`${BACKEND_URL}/api/workflows`),
        axios.get(`${BACKEND_URL}/api/workflows/executions/me`),
        axios.get(`${BACKEND_URL}/api/workflows/stats/me`)
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setMyWorkflows(workflowsRes.data.workflows || []);
      setExecutions(executionsRes.data.executions || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching workflows:", error);
      toast.error("Failed to load workflows");
    } finally {
      setLoading(false);
    }
  };

  const createFromTemplate = async (template) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/workflows/from-template`, {
        template_id: template.template_id
      });
      toast.success(`Workflow "${res.data.name}" created`);
      setMyWorkflows(prev => [res.data, ...prev]);
      setActiveTab("my-workflows");
    } catch (error) {
      toast.error("Failed to create workflow");
    }
  };

  const openExecuteDialog = (workflow) => {
    setSelectedWorkflow(workflow);
    // Initialize variables with defaults
    const vars = {};
    (workflow.variables || []).forEach(v => {
      vars[v.name] = v.default || "";
    });
    setExecutionVariables(vars);
    setShowExecuteDialog(true);
  };

  const executeWorkflow = async () => {
    if (!selectedWorkflow) return;
    
    setExecuting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/workflows/execute`, {
        workflow_id: selectedWorkflow.workflow_id,
        variables: executionVariables
      });
      
      toast.success("Workflow started!");
      setShowExecuteDialog(false);
      
      // Refresh executions
      const execRes = await axios.get(`${BACKEND_URL}/api/workflows/executions/me`);
      setExecutions(execRes.data.executions || []);
      setActiveTab("executions");
      
    } catch (error) {
      toast.error("Failed to start workflow");
    } finally {
      setExecuting(false);
    }
  };

  const deleteWorkflow = async (workflowId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/workflows/${workflowId}`);
      toast.success("Workflow deleted");
      setMyWorkflows(prev => prev.filter(w => w.workflow_id !== workflowId));
    } catch (error) {
      toast.error("Failed to delete workflow");
    }
  };

  const getIcon = (iconName) => {
    const Icon = iconMap[iconName] || Workflow;
    return Icon;
  };

  const getCategoryColor = (category) => {
    const colors = {
      contract: "bg-[#C5A059]/20 text-[#C5A059] border-[#C5A059]/30",
      research: "bg-[#4A6FA5]/20 text-[#4A6FA5] border-[#4A6FA5]/30",
      corporate: "bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30",
      drafting: "bg-[#8B5CF6]/20 text-[#8B5CF6] border-[#8B5CF6]/30",
      compliance: "bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]/30",
      custom: "bg-[#64748B]/20 text-[#64748B] border-[#64748B]/30"
    };
    return colors[category] || colors.custom;
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: "bg-[#10B981]/20 text-[#10B981]",
      running: "bg-[#3B82F6]/20 text-[#3B82F6]",
      failed: "bg-[#EF4444]/20 text-[#EF4444]",
      pending: "bg-[#F59E0B]/20 text-[#F59E0B]"
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="workflows-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
              className="text-[#94A3B8] hover:text-[#E2E8F0]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Scale className="w-6 h-6 text-[#C5A059]" />
              <span className="font-playfair text-lg font-semibold text-[#E2E8F0]">Workflow Builder</span>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
                <span className="text-[#94A3B8]">Completed: <span className="text-[#E2E8F0]">{stats.completed}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#3B82F6] animate-pulse"></div>
                <span className="text-[#94A3B8]">Running: <span className="text-[#E2E8F0]">{stats.running}</span></span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#15171C] border border-white/10 rounded-sm mb-6">
            <TabsTrigger 
              value="templates"
              data-testid="tab-templates"
              className="data-[state=active]:bg-[#C5A059]/10 data-[state=active]:text-[#C5A059] rounded-sm"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" /> Templates
            </TabsTrigger>
            <TabsTrigger 
              value="my-workflows"
              data-testid="tab-my-workflows"
              className="data-[state=active]:bg-[#C5A059]/10 data-[state=active]:text-[#C5A059] rounded-sm"
            >
              <Workflow className="w-4 h-4 mr-2" /> My Workflows ({myWorkflows.length})
            </TabsTrigger>
            <TabsTrigger 
              value="executions"
              data-testid="tab-executions"
              className="data-[state=active]:bg-[#C5A059]/10 data-[state=active]:text-[#C5A059] rounded-sm"
            >
              <Play className="w-4 h-4 mr-2" /> Executions ({executions.length})
            </TabsTrigger>
          </TabsList>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => {
                const Icon = getIcon(template.icon);
                return (
                  <Card 
                    key={template.template_id}
                    className="bg-[#15171C] border-white/5 rounded-sm p-5 hover:border-[#C5A059]/30 transition-all cursor-pointer group"
                    onClick={() => setSelectedTemplate(template)}
                    data-testid={`template-${template.template_id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-10 h-10 rounded-sm bg-[#C5A059]/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <Badge className={`${getCategoryColor(template.category)} font-mono text-xs`}>
                        {template.category}
                      </Badge>
                    </div>
                    
                    <h3 className="font-playfair text-lg text-[#E2E8F0] mb-2">{template.name}</h3>
                    <p className="text-[#94A3B8] text-sm font-manrope mb-4 line-clamp-2">
                      {template.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-[#64748B] text-xs font-mono">
                        <Clock className="w-3 h-3" />
                        {template.estimated_time}
                      </div>
                      <div className="flex items-center gap-2 text-[#64748B] text-xs font-mono">
                        {template.steps?.length || 0} steps
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full mt-4 bg-[#C5A059]/10 text-[#C5A059] hover:bg-[#C5A059]/20 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation();
                        createFromTemplate(template);
                      }}
                      data-testid={`use-template-${template.template_id}`}
                    >
                      <Plus className="w-4 h-4 mr-2" /> Use Template
                    </Button>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* My Workflows Tab */}
          <TabsContent value="my-workflows">
            {myWorkflows.length === 0 ? (
              <div className="text-center py-12">
                <Workflow className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                <h3 className="font-playfair text-lg text-[#E2E8F0] mb-2">No Workflows Yet</h3>
                <p className="text-[#94A3B8] mb-4">Create your first workflow from a template</p>
                <Button 
                  onClick={() => setActiveTab("templates")}
                  className="bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] rounded-sm"
                >
                  Browse Templates
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {myWorkflows.map((workflow) => (
                  <Card 
                    key={workflow.workflow_id}
                    className="bg-[#15171C] border-white/5 rounded-sm p-4 hover:border-[#C5A059]/30 transition-all"
                    data-testid={`workflow-${workflow.workflow_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-sm bg-[#C5A059]/10 flex items-center justify-center">
                          <Workflow className="w-5 h-5 text-[#C5A059]" />
                        </div>
                        <div>
                          <h3 className="font-playfair text-[#E2E8F0]">{workflow.name}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            <Badge className={`${getCategoryColor(workflow.category)} font-mono text-xs`}>
                              {workflow.category}
                            </Badge>
                            <span className="text-[#64748B] text-xs font-mono">
                              {workflow.steps?.length || 0} steps
                            </span>
                            <span className="text-[#64748B] text-xs font-mono">
                              Created {new Date(workflow.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          onClick={() => openExecuteDialog(workflow)}
                          className="bg-[#10B981] text-white hover:bg-[#059669] rounded-sm"
                          data-testid={`run-workflow-${workflow.workflow_id}`}
                        >
                          <Play className="w-4 h-4 mr-2" /> Run
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-[#94A3B8] hover:text-[#E2E8F0]">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-[#15171C] border-white/10">
                            <DropdownMenuItem 
                              onClick={() => deleteWorkflow(workflow.workflow_id)}
                              className="text-[#EF4444] focus:text-[#EF4444]"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Executions Tab */}
          <TabsContent value="executions">
            {executions.length === 0 ? (
              <div className="text-center py-12">
                <Play className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
                <h3 className="font-playfair text-lg text-[#E2E8F0] mb-2">No Executions Yet</h3>
                <p className="text-[#94A3B8]">Run a workflow to see execution history</p>
              </div>
            ) : (
              <div className="space-y-3">
                {executions.map((execution) => (
                  <Card 
                    key={execution.execution_id}
                    className="bg-[#15171C] border-white/5 rounded-sm p-4 cursor-pointer hover:border-[#C5A059]/30 transition-all"
                    onClick={() => navigate(`/workflows/execution/${execution.execution_id}`)}
                    data-testid={`execution-${execution.execution_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${getStatusColor(execution.status)}`}>
                          {execution.status === "completed" && <CheckCircle className="w-5 h-5" />}
                          {execution.status === "running" && <Loader2 className="w-5 h-5 animate-spin" />}
                          {execution.status === "failed" && <XCircle className="w-5 h-5" />}
                        </div>
                        <div>
                          <h3 className="font-playfair text-[#E2E8F0]">{execution.workflow_name}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            <Badge className={`${getStatusColor(execution.status)} font-mono text-xs`}>
                              {execution.status}
                            </Badge>
                            <span className="text-[#64748B] text-xs font-mono">
                              Step {execution.current_step}/{execution.total_steps}
                            </span>
                            <span className="text-[#64748B] text-xs font-mono">
                              {new Date(execution.started_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-[#64748B]" />
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Template Detail Dialog */}
      <Dialog open={!!selectedTemplate} onOpenChange={() => setSelectedTemplate(null)}>
        <DialogContent className="bg-[#15171C] border-white/10 max-w-2xl">
          {selectedTemplate && (
            <>
              <DialogHeader>
                <DialogTitle className="font-playfair text-xl text-[#E2E8F0] flex items-center gap-3">
                  {(() => {
                    const Icon = getIcon(selectedTemplate.icon);
                    return <Icon className="w-6 h-6 text-[#C5A059]" />;
                  })()}
                  {selectedTemplate.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <p className="text-[#94A3B8] font-manrope">{selectedTemplate.description}</p>
                
                <div className="flex items-center gap-4">
                  <Badge className={`${getCategoryColor(selectedTemplate.category)} font-mono`}>
                    {selectedTemplate.category}
                  </Badge>
                  <span className="text-[#64748B] text-sm font-mono flex items-center gap-1">
                    <Clock className="w-4 h-4" /> {selectedTemplate.estimated_time}
                  </span>
                </div>
                
                <div>
                  <h4 className="text-[#E2E8F0] font-medium mb-3">Workflow Steps</h4>
                  <div className="space-y-2">
                    {selectedTemplate.steps?.map((step, idx) => (
                      <div key={step.step_id} className="flex items-start gap-3 p-3 bg-[#0B0C10] rounded-sm">
                        <div className="w-6 h-6 rounded-full bg-[#C5A059]/20 text-[#C5A059] flex items-center justify-center text-xs font-mono">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="text-[#E2E8F0] text-sm font-medium">{step.name}</p>
                          <p className="text-[#64748B] text-xs">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {selectedTemplate.variables?.length > 0 && (
                  <div>
                    <h4 className="text-[#E2E8F0] font-medium mb-2">Required Inputs</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.variables.map(v => (
                        <Badge key={v.name} variant="outline" className="text-[#94A3B8] border-white/20 font-mono text-xs">
                          {v.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedTemplate(null)} className="border-white/20 text-[#E2E8F0]">
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    createFromTemplate(selectedTemplate);
                    setSelectedTemplate(null);
                  }}
                  className="bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37]"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Workflow
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Execute Workflow Dialog */}
      <Dialog open={showExecuteDialog} onOpenChange={setShowExecuteDialog}>
        <DialogContent className="bg-[#15171C] border-white/10 max-w-lg">
          {selectedWorkflow && (
            <>
              <DialogHeader>
                <DialogTitle className="font-playfair text-xl text-[#E2E8F0]">
                  Run: {selectedWorkflow.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {selectedWorkflow.variables?.length > 0 ? (
                  selectedWorkflow.variables.map((variable) => (
                    <div key={variable.name} className="space-y-2">
                      <Label className="text-[#E2E8F0]">
                        {variable.label}
                        {variable.required && <span className="text-[#EF4444] ml-1">*</span>}
                      </Label>
                      
                      {variable.type === "select" ? (
                        <Select
                          value={executionVariables[variable.name] || variable.default || ""}
                          onValueChange={(val) => setExecutionVariables(prev => ({ ...prev, [variable.name]: val }))}
                        >
                          <SelectTrigger className="bg-[#0B0C10] border-white/10 text-[#E2E8F0]">
                            <SelectValue placeholder={`Select ${variable.label}`} />
                          </SelectTrigger>
                          <SelectContent className="bg-[#15171C] border-white/10">
                            {variable.options?.map(opt => (
                              <SelectItem key={opt} value={opt} className="text-[#E2E8F0]">
                                {opt.charAt(0).toUpperCase() + opt.slice(1).replace(/_/g, ' ')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : variable.type === "textarea" ? (
                        <Textarea
                          value={executionVariables[variable.name] || ""}
                          onChange={(e) => setExecutionVariables(prev => ({ ...prev, [variable.name]: e.target.value }))}
                          placeholder={variable.placeholder || `Enter ${variable.label}`}
                          className="bg-[#0B0C10] border-white/10 text-[#E2E8F0] min-h-[100px]"
                        />
                      ) : (
                        <Input
                          value={executionVariables[variable.name] || ""}
                          onChange={(e) => setExecutionVariables(prev => ({ ...prev, [variable.name]: e.target.value }))}
                          placeholder={variable.placeholder || `Enter ${variable.label}`}
                          className="bg-[#0B0C10] border-white/10 text-[#E2E8F0]"
                        />
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-[#94A3B8]">This workflow has no required inputs. Ready to run!</p>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowExecuteDialog(false)} className="border-white/20 text-[#E2E8F0]">
                  Cancel
                </Button>
                <Button 
                  onClick={executeWorkflow}
                  disabled={executing}
                  className="bg-[#10B981] text-white hover:bg-[#059669]"
                >
                  {executing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" /> Run Workflow
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Workflows;
